"use client";
/**
 * Attribute components — the building blocks of blocks.
 *
 * On the public site these render plain markup and nothing else: no state, no
 * effects, no editor code. Inside the page builder (`ctx.editing`) each one
 * swaps to a lazily-loaded editable implementation from `./attributes.editable`
 * — that chunk (contentEditable machinery, media/link/icon pickers, the
 * admin-UI translation dictionaries) never reaches a visitor.
 */
import dynamic from "next/dynamic";
import { createElement, type ReactNode } from "react";
import { useAttr, useBlock, BlockProvider, type AttrPath } from "./context";
import { lv } from "../i18n";
import { toPlainText } from "../plain-text";
import { safeHref } from "../safe-href";
import { Icon } from "./Icon";
import { PREVIEW_IMAGE, PREVIEW_REPEATER_ITEMS, previewText } from "./preview-placeholders";
import type { Localized, LinkValue } from "../types";
import type { ImageValue, VideoValue } from "../media-shared";
import type {
  TextAttrProps,
  ImageAttrProps,
  VideoAttrProps,
  IconAttrProps,
  LinkAttrProps,
  RepeaterAttrProps,
  SelectAttrProps,
} from "./attributes.types";

export type { RootElementProps } from "./attributes.types";
export { AdvancedImageAttr } from "./AdvancedImageAttr";
export type { AdvancedImageAttrProps } from "./AdvancedImageAttr";

// Editing UI lives in a separate chunk pulled in only inside the builder.
const EditableText = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableText })), { ssr: false });
const EditableImage = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableImage })), { ssr: false });
const EditableVideo = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableVideo })), { ssr: false });
const EditableIcon = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableIcon })), { ssr: false });
const EditableLink = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableLink })), { ssr: false });
const EditableRepeater = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableRepeater })), { ssr: false });
const EditableSelect = dynamic(() => import("./attributes.editable").then((m) => ({ default: m.EditableSelect })), { ssr: false });

/* ---------------------------------- text --------------------------------- */

function stripTags(html: string): string {
  // Decode entities too: a plain-text field edited while it once held rich text
  // can carry a sanitized "&amp;" that would otherwise render literally.
  return toPlainText(html);
}

/**
 * Localized (rich) text. `as` controls the tag; use <Heading> for automatic
 * h1/h2 ordering.
 */
export function TextAttr(props: TextAttrProps) {
  const { name, as, rich, className, placeholder, ...elementProps } = props;
  const ctx = useBlock();
  const [value] = useAttr<Localized>(name);
  const tag = as || (rich ? "div" : "span");
  // lv() can return the stored value verbatim (e.g. an empty array) when it
  // isn't a proper locale map. Only a string is ever valid rendered text —
  // see board issue 18 for how a non-object empty value reaches here.
  const resolved = lv(value, ctx.locale, "");
  let html = typeof resolved === "string" ? resolved : "";

  if (!ctx.editing) {
    // Block chooser samples show the attribute's placeholder (or a generic stand-in) instead of nothing.
    if (!html && ctx.sample) {
      const fallback = previewText(ctx.locale);
      const own = typeof placeholder === "string" ? placeholder : lv(placeholder, ctx.locale, "");
      html = rich ? (own ? `<p>${own}</p>` : fallback.rich) : own || (/^h[1-6]$/.test(String(as)) ? fallback.heading : fallback.text);
    }
    if (!html) return null;
    if (rich) {
      return createElement(tag, { ...elementProps, className, dangerouslySetInnerHTML: { __html: html } });
    }
    return createElement(tag, { ...elementProps, className }, stripTags(html));
  }
  return <EditableText {...props} />;
}

/**
 * SEO-friendly automatic heading: the first block on a page renders h1,
 * every following block renders h2 (use `sub` for h3 inside a block).
 */
export function Heading(props: Omit<TextAttrProps, "as" | "rich"> & { sub?: boolean }) {
  const ctx = useBlock();
  const tag = props.sub ? "h3" : ctx.blockIndex === 0 ? "h1" : "h2";
  const { sub: _sub, ...textProps } = props;
  return <TextAttr {...textProps} as={tag} />;
}

/* --------------------------------- image --------------------------------- */

/**
 * Whether an image renders eager/high priority. `"auto"` keeps the classic
 * first-block heuristic, but skips repeater items after the first one (e.g.
 * offscreen slider slides) so they don't compete with the visible LCP image.
 */
function imagePriority(priority: ImageAttrProps["priority"], blockIndex: number, scope: AttrPath): boolean {
  if (priority === true || priority === false) return priority;
  if (blockIndex !== 0) return false;
  // Repeater scopes look like [..., name, index]; any index > 0 is a later item.
  return !scope.some((segment) => typeof segment === "number" && segment > 0);
}

export function ImageAttr(props: ImageAttrProps) {
  const { name, className, sizes, style, imageStyle, priority, editorControlPlacement: _p, ...elementProps } = props;
  const ctx = useBlock();
  const [value] = useAttr<ImageValue | null>(name);
  const eager = imagePriority(priority, ctx.blockIndex, ctx.scope);

  // Block chooser samples show a neutral illustration for an empty image slot.
  const shown: ImageValue | null = value?.src ? value : ctx.sample && !ctx.editing ? { id: 0, src: PREVIEW_IMAGE, width: 1200, height: 800 } as ImageValue : null;
  const img = shown?.src ? (
    <picture {...elementProps} className={`cms-picture ${className || ""}`} style={style}>
      {shown.srcsetAvif && <source type="image/avif" srcSet={shown.srcsetAvif} sizes={sizes || "100vw"} />}
      {shown.srcsetWebp && <source type="image/webp" srcSet={shown.srcsetWebp} sizes={sizes || "100vw"} />}
      <img
        src={shown.src}
        style={imageStyle}
        alt={lv(shown.alt, ctx.locale, "") || ""}
        width={shown.width || undefined}
        height={shown.height || undefined}
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : undefined}
        decoding="async"
      />
    </picture>
  ) : null;

  if (!ctx.editing) return img;
  return <EditableImage {...props} img={img} />;
}

/* --------------------------------- video --------------------------------- */

export function VideoAttr(props: VideoAttrProps) {
  const { name, className, poster, autoPlay = true, muted = true, loop = true, playsInline = true, preload = "metadata", ...videoProps } = props;
  const ctx = useBlock();
  const [value] = useAttr<VideoValue | null>(name);

  const video = value?.src ? (
    <video
      {...videoProps}
      className={className}
      src={value.src}
      poster={poster ?? value.poster}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      preload={preload}
    />
  ) : null;

  if (!ctx.editing) return video;
  return <EditableVideo {...props} video={video} />;
}

/* ---------------------------------- icon ---------------------------------- */

export function IconAttr(props: IconAttrProps) {
  const { name, className, ...elementProps } = props;
  const ctx = useBlock();
  const [value] = useAttr<string>(name);

  if (!ctx.editing) return <Icon {...elementProps} name={value} className={className} />;
  return <EditableIcon {...props} />;
}

/* ---------------------------------- link ---------------------------------- */

export function LinkAttr(props: LinkAttrProps) {
  const { name, className, children, ariaLabel, optional, ...anchorProps } = props;
  const ctx = useBlock();
  const [value] = useAttr<LinkValue | null>(name);
  const label = lv(value?.label, ctx.locale, "") || (ctx.sample && !ctx.editing ? previewText(ctx.locale).link : "");
  const content = children ?? label ?? "";

  if (!ctx.editing) {
    // Unsafe schemes (javascript:, data:, …) are dropped at render time
    const href = safeHref(value?.href);
    if (!href) return optional && !ctx.sample ? null : <span {...anchorProps} className={className}>{content}</span>;
    return (
      <a
        {...anchorProps}
        className={className}
        href={href}
        target={value?.newTab ? "_blank" : undefined}
        rel={value?.newTab ? "noopener noreferrer" : undefined}
        aria-label={ariaLabel || anchorProps["aria-label"]}
      >
        {content}
      </a>
    );
  }
  return <EditableLink {...props} />;
}

/* -------------------------------- repeater -------------------------------- */

export function RepeaterAttr(props: RepeaterAttrProps) {
  const { name, children: renderChildren, itemDefaults: _d, addLabel: _a, className, as, ...elementProps } = props;
  const ctx = useBlock();
  const [items] = useAttr<Record<string, unknown>[]>(name);
  // Block chooser samples show a few placeholder items for an empty list.
  const list = Array.isArray(items) && items.length ? items : ctx.sample && !ctx.editing ? Array.from({ length: PREVIEW_REPEATER_ITEMS }, () => ({})) : [];

  if (ctx.editing) return <EditableRepeater {...props} />;

  const children: ReactNode = (
    <>
      {list.map((_, i) => {
        const scope: AttrPath = [...ctx.scope, name, i];
        return (
          <BlockProvider key={i} value={{ ...ctx, scope }}>
            {renderChildren(i)}
          </BlockProvider>
        );
      })}
    </>
  );
  return createElement(as || "div", { ...elementProps, className }, children);
}

/* ------------------------------ select (enum) ----------------------------- */

/** Edit-only inline select for layout choices (e.g. image left/right). */
export function SelectAttr(props: SelectAttrProps) {
  const ctx = useBlock();
  if (!ctx.editing) return null;
  return <EditableSelect {...props} />;
}

/** Read a select/scalar attribute value inside a block component. */
export function useAttrValue<T = string>(name: string, fallback: T): T {
  const [value] = useAttr<T>(name);
  return value ?? fallback;
}
